/**
 * Generic report row exporters.
 *
 * Reports are schema-driven so the exporter only needs the column
 * metadata + the raw rows. We support two formats:
 *
 *   - CSV: standard RFC-4180 quoting, opens cleanly in Excel + Sheets.
 *   - Excel (.xls): an HTML payload served with the
 *     ``application/vnd.ms-excel`` mime type. Excel parses HTML tables
 *     natively, so we get a real .xls without pulling in a SheetJS-class
 *     dependency.
 *
 * Hidden columns are honored by default so the file matches what the
 * user sees on screen. Pass ``includeHidden: true`` if you want the full
 * data set regardless. Rows that carry a ``staff_breakdown`` are
 * flattened in place: each parent is followed by its breakdown rows so
 * the export reflects an expanded table view.
 */

import moment from "moment";
import { ReportColumn, ReportDefinition } from "../types/reports";
import { formatCurrency } from "./cashDrawerUtils";
import { isServiceCommissionOverridesEnabled } from "./serviceCommissionOverridesFlag";

interface ExportOptions {
  filename?: string;
  /** Used when ``filename`` isn't supplied. Defaults to definition.name. */
  customName?: string;
  hiddenColumns?: string[];
  includeHidden?: boolean;
}

function visibleColumns(
  definition: ReportDefinition,
  hidden: string[] | undefined,
  includeHidden: boolean,
): ReportColumn[] {
  if (includeHidden) return definition.columns;
  const hiddenSet = new Set(hidden ?? []);
  return definition.columns.filter((c) => !hiddenSet.has(c.key));
}

/**
 * Expand each row into ``[parent, ...child rows]`` so the export
 * reflects what the user sees with every row expanded. Children come
 * from ``staff_breakdown`` for staff-breakdown reports and from
 * ``line_items`` for the Transaction Log. The "name slot" — the
 * column where the child's display name goes — depends on which kind
 * of breakdown we have:
 *
 *  - staff_breakdown: the parent's grouping column. The child carries
 *    its name in ``staff_member``.
 *  - line_items: the ``items_sold`` column (so the description aligns
 *    with the parent's Services Provided / Products Sold roll-up).
 *    The child carries its name in that same column key.
 *
 * Every other column reads the same key from the child entry.
 */
function flattenWithBreakdowns(
  definition: ReportDefinition,
  rows: Array<Record<string, unknown>>,
): Array<Record<string, unknown>> {
  const groupingCol =
    definition.columns.find((c) => c.is_grouping) ?? definition.columns[0];
  const out: Array<Record<string, unknown>> = [];
  for (const row of rows) {
    out.push(row);
    const isLineItemBreakdown = Array.isArray(row.line_items);
    const breakdown = (row.line_items ?? row.staff_breakdown ?? []) as Array<
      Record<string, unknown>
    >;
    const nameColKey = isLineItemBreakdown
      ? (definition.columns.find((c) => c.key === "items_sold")?.key ??
        groupingCol?.key)
      : groupingCol?.key;
    if (breakdown.length === 0) continue;
    for (const b of breakdown) {
      const child: Record<string, unknown> = {};
      for (const col of definition.columns) {
        if (nameColKey && col.key === nameColKey) {
          // Two-space indent so the child entry visually nests under
          // its parent in the exported file. Line-item children keep
          // their description in items_sold; staff breakdown
          // children store it in `staff_member`.
          const display = b[col.key] ?? b.staff_member ?? "";
          child[col.key] = `  ${display}`;
        } else if (col.key in b) {
          child[col.key] = b[col.key];
        }
      }
      out.push(child);
    }
  }
  return out;
}

function formatCellForExport(
  value: unknown,
  type: ReportColumn["type"],
  accountingNegative = false,
): string {
  if (value === null || value === undefined) return "";
  if (type === "currency") {
    const numeric =
      typeof value === "number" ? value : parseFloat(String(value));
    if (Number.isNaN(numeric)) return "";
    // Refund/contra columns arrive as a positive magnitude but represent
    // money out — export them as a real negative so spreadsheet sums are
    // correct. ((-0).toFixed(2) is "0.00", so a zero refund stays "0.00".)
    return (accountingNegative ? -numeric : numeric).toFixed(2);
  }
  if (type === "int") {
    return String(value);
  }
  if (type === "decimal") {
    const numeric =
      typeof value === "number" ? value : parseFloat(String(value));
    if (Number.isNaN(numeric)) return "";
    return numeric.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (type === "datetime" || type === "date") {
    if (!value) return "";
    return moment(String(value)).format(
      type === "datetime" ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD",
    );
  }
  return String(value);
}

export interface BreakdownExportTable {
  label: string;
  header: string[];
  body: string[][];
}

/**
 * The definition's own-column drill-down (``definition.breakdown``, e.g. Staff
 * Earnings → "Commission by service") as a flat table for export: one row per
 * child, prefixed with its parent's grouping value (the staff member). Null
 * when the report has none, no row has children, or the feature is off.
 */
export function breakdownExportTable(
  definition: ReportDefinition,
  rows: Array<Record<string, unknown>>,
): BreakdownExportTable | null {
  const spec = definition.breakdown;
  if (!spec || !isServiceCommissionOverridesEnabled()) return null;
  const groupingCol =
    definition.columns.find((c) => c.is_grouping) ?? definition.columns[0];
  const body: string[][] = [];
  for (const row of rows) {
    const parentName = String(row[groupingCol?.key] ?? "");
    const children = (row[spec.field] ?? []) as Array<Record<string, unknown>>;
    for (const child of children) {
      body.push([
        parentName,
        ...spec.columns.map((c) =>
          formatCellForExport(child[c.key], c.type, c.accounting_negative),
        ),
      ]);
    }
  }
  if (body.length === 0) return null;
  return {
    label: spec.label,
    header: [groupingCol?.label ?? "", ...spec.columns.map((c) => c.label)],
    body,
  };
}

function csvEscape(value: string): string {
  if (
    value.includes(",") ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function triggerDownload(blob: Blob, filename: string): void {
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function defaultFilename(
  definition: ReportDefinition,
  ext: string,
  custom?: string,
): string {
  const slug = (custom || definition.name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return `${slug}-${moment().format("YYYY-MM-DD")}.${ext}`;
}

export function exportReportToCSV(
  definition: ReportDefinition,
  rows: Array<Record<string, unknown>>,
  opts: ExportOptions = {},
): void {
  const columns = visibleColumns(
    definition,
    opts.hiddenColumns,
    opts.includeHidden ?? false,
  );
  const expandedRows = flattenWithBreakdowns(definition, rows);
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const body = expandedRows
    .map((row) =>
      columns
        .map((c) =>
          csvEscape(
            formatCellForExport(row[c.key], c.type, c.accounting_negative),
          ),
        )
        .join(","),
    )
    .join("\n");
  const detail = breakdownExportTable(definition, rows);
  const detailCsv = detail
    ? `\n\n${csvEscape(detail.label)}\n` +
      [detail.header, ...detail.body]
        .map((cells) => cells.map(csvEscape).join(","))
        .join("\n")
    : "";
  const csv = `${header}\n${body}${detailCsv}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  triggerDownload(
    blob,
    opts.filename || defaultFilename(definition, "csv", opts.customName),
  );
}

function htmlEscape(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** A second Excel table for the drill-down, or "" when there is none. */
function detailHtml(detail: BreakdownExportTable | null): string {
  if (!detail) return "";
  const head = detail.header.map((h) => `<th>${htmlEscape(h)}</th>`).join("");
  const body = detail.body
    .map(
      (cells) =>
        `<tr>${cells.map((c) => `<td>${htmlEscape(c)}</td>`).join("")}</tr>`,
    )
    .join("");
  return `
    <br />
    <h3>${htmlEscape(detail.label)}</h3>
    <table border="1">
      <thead><tr>${head}</tr></thead>
      <tbody>${body}</tbody>
    </table>`;
}

export function exportReportToExcel(
  definition: ReportDefinition,
  rows: Array<Record<string, unknown>>,
  opts: ExportOptions = {},
): void {
  const columns = visibleColumns(
    definition,
    opts.hiddenColumns,
    opts.includeHidden ?? false,
  );
  const expandedRows = flattenWithBreakdowns(definition, rows);

  const header = columns.map((c) => `<th>${htmlEscape(c.label)}</th>`).join("");
  const body = expandedRows
    .map(
      (row) =>
        "<tr>" +
        columns
          .map(
            (c) =>
              `<td>${htmlEscape(
                formatCellForExport(row[c.key], c.type, c.accounting_negative),
              )}</td>`,
          )
          .join("") +
        "</tr>",
    )
    .join("");

  // Excel reads HTML tables when the .xls extension + spreadsheet mime
  // type are paired. The xmlns lines are required for Excel to treat
  // it as a workbook rather than plain HTML.
  const html = `
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="UTF-8" />
    <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>${htmlEscape(definition.name)}</x:Name>
              <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
    <![endif]-->
  </head>
  <body>
    <table border="1">
      <thead><tr>${header}</tr></thead>
      <tbody>${body}</tbody>
    </table>${detailHtml(breakdownExportTable(definition, rows))}
  </body>
</html>`.trim();

  const blob = new Blob([html], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  triggerDownload(
    blob,
    opts.filename || defaultFilename(definition, "xls", opts.customName),
  );
}

/**
 * Format a single cell for display in the table view.
 *
 * Kept here next to the export formatters so the on-screen number
 * format always matches the downloaded one.
 */
export function formatCellForDisplay(
  value: unknown,
  type: ReportColumn["type"],
  accountingNegative = false,
): string {
  if (value === null || value === undefined || value === "") return "-";
  if (type === "currency") {
    const numeric =
      typeof value === "number" ? value : parseFloat(String(value));
    if (Number.isNaN(numeric)) return "-";
    // formatCurrency adds thousands separators ("$1,234.50"); the null/NaN
    // guard above keeps the report's "-" filler for empty cells. Refund/
    // contra columns arrive as a positive magnitude but represent money out,
    // so `contra` renders them in accounting parentheses "($1,234.50)".
    return formatCurrency(numeric, { contra: accountingNegative });
  }
  if (type === "int") {
    const numeric =
      typeof value === "number" ? value : parseInt(String(value), 10);
    if (Number.isNaN(numeric)) return "-";
    return numeric.toLocaleString();
  }
  if (type === "decimal") {
    const numeric =
      typeof value === "number" ? value : parseFloat(String(value));
    if (Number.isNaN(numeric)) return "-";
    return numeric.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (type === "date") {
    return moment(String(value)).format("MMM D, YYYY");
  }
  if (type === "datetime") {
    return moment(String(value)).format("MMM D, YYYY h:mm A");
  }
  return String(value);
}
